import '../../../../../packages/neb-lit-components/src/components/neb-popup-header';
import '../../../../../packages/neb-lit-components/src/components/neb-table-add';
import '../../../../../packages/neb-lit-components/src/components/neb-pagination';
import '../../../../../packages/neb-lit-components/src/components/neb-action-bar';
import '../../../../../packages/neb-lit-components/src/components/inputs/neb-textfield';
import '../../../../../packages/neb-lit-components/src/components/inputs/neb-select';
import '../../../../../packages/neb-lit-components/src/components/controls/neb-switch';
import { css, html } from 'lit';

import {
  getPatientsWithOpenCharges,
  getEraEobLineItemDetails,
} from '../../../../../packages/neb-api-client/src/ledger/line-items';
import * as patientApiClient from '../../../../../packages/neb-api-client/src/patient-api-client';
import { POPOVER_POSITION } from '../../../../../packages/neb-lit-components/src/components/neb-date-picker';
import Overlay from '../../../../../packages/neb-lit-components/src/components/overlays/neb-overlay';
import { sortRawPatients } from '../../../../../packages/neb-lit-components/src/components/overlays/payment/util';
import { calculatePageCount } from '../../../../../packages/neb-lit-components/src/utils/pagination';
import { store } from '../../../../../packages/neb-redux/neb-redux-store';
import { LocationsService } from '../../../../../packages/neb-redux/services/locations';
import { parseDate } from '../../../../../packages/neb-utils/date-util';
import {
  centsToCurrency,
  HIDE_PREFERRED_OPTS,
  objToName,
} from '../../../../../packages/neb-utils/formatters';
import { mapPatientName } from '../../../../../packages/neb-utils/patient';
import { deduplicate } from '../../../../../packages/neb-www-booking/src/components/util/enable-booking-util';
import { associateCharges as associateEobCharges } from '../../../../api-clients/eob';
import { associateCharges as associateEraCharges } from '../../../../api-clients/era';
import { openError, openSuccess } from '../../../../store';
import {
  CSS_COLOR_GREY_1,
  CSS_FONT_SIZE_BODY,
  CSS_SPACING,
} from '../../../../styles/index';

export const ELEMENTS = {
  header: { id: 'associate-charge-header' },
  form: { id: 'associate-charge-form' },
  table: { id: 'associate-charge-table' },
  description: { id: 'associate-charge-description' },
  actionBar: { id: 'associate-charge-action-bar' },
  paginationControl: { id: 'associate-charge-pagination-control' },
  dateOfTransactionFrom: { id: 'date-of-transaction-from' },
  dateOfTransactionTo: { id: 'date-of-transaction-to' },
  selectLocations: { id: 'select-locations' },
  additionalChargesFilter: { id: 'additional-charges-filter' },
  applyButton: { id: 'apply-button' },
  selectPatient: { id: 'select-patient' },
  buttonAdditionalCharges: { id: 'button-additional-charges' },
  applyFilterButton: { id: 'apply-filter-button' },
};

const ITEM_EMPTY = {
  data: { id: '' },
  label: '',
};

const TABLE_CONFIG = [
  {
    key: 'patientName',
    label: 'Patient',
    flex: css`1 0 0`,
    truncate: true,
  },
  {
    key: 'dateOfService',
    label: 'Date of Service',
    flex: css`0.5 0 0`,
    truncate: true,
  },
  {
    key: 'description',
    label: 'Description',
    flex: css`1.5 0 0`,
    truncate: true,
  },
  {
    key: 'billedAmount',
    label: 'Billed Amount',
    flex: css`1 0 0`,
    formatter: v => centsToCurrency(v),
  },
];

const uniq = arr => [...new Set(arr)];

function formatPatientsForDropdown(rawPatients, patientIdsInDropdown) {
  const filtered = rawPatients.filter(p => patientIdsInDropdown.includes(p.id));

  const sorted = sortRawPatients(filtered);

  return sorted.map(raw => ({
    label: objToName(mapPatientName(raw), HIDE_PREFERRED_OPTS),
    data: { id: raw.id },
  }));
}

class NebOverlayAssociateEraEobCharges extends Overlay {
  static get properties() {
    return {
      __allAdded: Boolean,
      __charges: Array,
      __selectedCharges: Array,
      __itemFilters: Object,
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .container {
          display: flex;
          flex-direction: column;
        }

        .content {
          width: 1024px;
        }

        .main-header {
          display: flex;
          flex-direction: column;
          padding: ${CSS_SPACING} ${CSS_SPACING} 0;
        }

        .description {
          padding: 5px 0 0 ${CSS_SPACING};
        }

        .add-button {
          padding: ${CSS_SPACING};
        }

        .pagination {
          display: flex;
          justify-content: flex-end;
          margin: ${CSS_SPACING};
          flex-shrink: 0;
        }

        .container-table {
          overflow-y: auto;
        }

        .filters {
          display: flex;
          gap: ${CSS_SPACING};
          padding: ${CSS_SPACING};
        }

        .align-self-center {
          align-self: center;
        }

        .no-items-message {
          font-size: ${CSS_FONT_SIZE_BODY};
          font-style: italic;
          color: ${CSS_COLOR_GREY_1};
          padding: 10px ${CSS_SPACING};
        }
      `,
    ];
  }

  constructor() {
    super();

    this.__initServices();
  }

  initState() {
    super.initState();

    this.__locations = [];

    this.__allAdded = false;
    this.__charges = [];
    this.__selectedCharges = [];
    this.__existingLineItemIds = new Set();
    this.__patients = [];
    this.__currentPage = 0;
    this.model = [];

    this.__itemFilters = {
      dateOfTransactionFrom: null,
      dateOfTransactionTo: null,
      patient: ITEM_EMPTY,
      locations: [],
      additionalCharges: false,
    };
  }

  __initServices() {
    this.__locationsService = new LocationsService(({ userLocations }) => {
      this.__locations = this.__formatLocations(userLocations);

      this.__itemFilters = {
        ...this.__itemFilters,
        locations: this.__locations,
      };
    });
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      apply: async () => {
        try {
          // Filter out charges that are already associated (existing line items)
          const newlyAddedCharges = this.__selectedCharges.filter(
            charge => !this.__existingLineItemIds.has(charge.id),
          );

          if (newlyAddedCharges.length === 0) {
            this.dismiss(false);
            return;
          }

          if (this.model.type === 'ERA') {
            await associateEraCharges({
              eraId: this.model.id,
              reportId: this.model.reportId,
              lineItemIds: newlyAddedCharges.map(c => c.id),
            });
          } else {
            await associateEobCharges({
              eobId: this.model.id,
              lineItemIds: newlyAddedCharges.map(c => c.id),
            });
          }

          store.dispatch(openSuccess('Charges associated successfully'));

          this.dismiss(true);
        } catch (e) {
          console.error(e);
          store.dispatch(openError('Error associating charges'));
        }
      },
      addAll: () => {
        const allItems = [...this.__selectedCharges, ...this.__charges];

        this.__selectedCharges = deduplicate(allItems, 'id');
      },
      add: item => {
        this.__selectedCharges = [...this.__selectedCharges, item];
      },
      changePage: async pageIndex => {
        this.__currentPage = pageIndex;

        await this.__fetchCharges();
      },
      itemsFilterChange: ({ name, value }) => {
        const newItemFilters = {
          ...this.__itemFilters,
          [name]: value,
        };

        this.__itemFilters = newItemFilters;

        if (name === 'dateOfTransactionFrom') {
          this.__updateDateOfTransactionToSelectable();
        }

        if (name === 'dateOfTransactionTo') {
          this.__updateDateOfTransactionFromSelectable();
        }
      },
      applyFilter: async () => {
        await this.__fetchCharges();
      },
    };

    this.__updateDateOfTransactionFromSelectable();
    this.__updateDateOfTransactionToSelectable();
  }

  async __fetchCharges() {
    const txDates = this.__getTransactionDates();

    const params = {
      locationIds: this.__itemFilters.locations.map(l => l.data.id),
      patientId: this.__itemFilters.patient.data.id,
      additionalCharges: this.__itemFilters.additionalCharges,
      payerPlanId: this.model.payment.payerPlanId,
      ...(txDates.dateOfTransactionFrom && {
        dateOfServiceFrom: txDates.dateOfTransactionFrom.toISOString(),
      }),
      ...(txDates.dateOfTransactionTo && {
        dateOfServiceTo: txDates.dateOfTransactionTo.toISOString(),
      }),
      offset: this.__currentPage * 10,
    };

    const lineItems = await getEraEobLineItemDetails(params);

    this.__charges = lineItems;
  }

  async connectedCallback() {
    this.__locationsService.connect();

    super.connectedCallback();

    await this.__fetchPatients();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.__locationsService.disconnect();
  }

  __updateDateOfTransactionFromSelectable() {
    this.handlers.dateOfTransactionFromSelectable = date =>
      (this.__itemFilters.dateOfTransactionTo === null ||
        date.isSameOrBefore(this.__itemFilters.dateOfTransactionTo)) &&
      date.isSameOrBefore(parseDate().endOf('day'));
  }

  __updateDateOfTransactionToSelectable() {
    this.handlers.dateOfTransactionToSelectable = date =>
      (this.__itemFilters.dateOfTransactionFrom === null ||
        date.isSameOrAfter(this.__itemFilters.dateOfTransactionFrom)) &&
      date.isSameOrBefore(parseDate().endOf('day'));
  }

  async __getPatientsHash(patientIds) {
    const uniqueIds = uniq(patientIds).filter(id => id);

    const patientsList = await patientApiClient.fetchSome(
      uniqueIds,
      {},
      true,
      true,
    );

    return patientsList.reduce((memo, patient) => {
      memo[patient.id] = patient;
      return memo;
    }, {});
  }

  async __rebuildPatientList(patientIdsInDropDown) {
    const patientIds = [
      ...patientIdsInDropDown,
      ...(this.model.patientId ? [this.model.patientId] : []),
    ];

    this.__patientsHash = await this.__getPatientsHash(patientIds);

    return formatPatientsForDropdown(
      Object.values(this.__patientsHash),
      patientIdsInDropDown,
    );
  }

  __getParamsForOutstandingCharges(payment) {
    const isPayerPayment = !!payment.payerPlanId;
    const isDiscountPayment = !!payment.codeDiscountId;
    return {
      ...(isPayerPayment
        ? { payerPlanId: payment.payerPlanId }
        : { noPayerPlan: true }),
      ...(payment.patientId ? { patientId: payment.patientId } : {}),
      ...(payment.patientId && !isPayerPayment && !isDiscountPayment
        ? { useRelationships: true }
        : {}),
    };
  }

  __getTransactionDates() {
    return {
      ...(this.__itemFilters.dateOfTransactionFrom
        ? {
            dateOfTransactionFrom: parseDate(
              this.__itemFilters.dateOfTransactionFrom,
            ).startOf('day'),
          }
        : { dateOfTransactionFrom: null }),
      ...(this.__itemFilters.dateOfTransactionTo
        ? {
            dateOfTransactionTo: parseDate(
              this.__itemFilters.dateOfTransactionTo,
            ).endOf('day'),
          }
        : { dateOfTransactionTo: null }),
    };
  }

  async __fetchPatients() {
    const patientsWithOpenCharges = await getPatientsWithOpenCharges({
      params: this.__getParamsForOutstandingCharges(this.model.payment),
    });

    const patientIdsInDropdown =
      patientsWithOpenCharges.length > 0
        ? patientsWithOpenCharges
        : [
            ...(this.model.payment.patientId
              ? [this.model.payment.patientId]
              : []),
          ];

    const patientsInDropdown =
      await this.__rebuildPatientList(patientIdsInDropdown);

    this.__patients = [ITEM_EMPTY, ...patientsInDropdown];
  }

  __formatLocations(locations) {
    return locations.map(location => ({
      data: location,
      label: location.name,
    }));
  }

  __refreshAllAdded() {
    this.__allAdded = this.__charges.length
      ? this.__charges.every(i =>
          this.__selectedCharges.some(c => c.id === i.id),
        )
      : null;
  }

  update(changedProps) {
    if (changedProps.has('model') && this.model) {
      const { selectedLineItems } = this.model;
      this.__selectedCharges = selectedLineItems;

      selectedLineItems.forEach(item => {
        this.__existingLineItemIds.add(item.id);
      });
    }

    if (
      changedProps.has('__charges') ||
      changedProps.has('__selectedCharges')
    ) {
      this.__refreshAllAdded();
    }

    super.update(changedProps);
  }

  renderHeader() {
    return html`
      <neb-popup-header
        id="${ELEMENTS.header.id}"
        class="main-header"
        .onCancel="${this.handlers.dismiss}"
        title="Associate ERA/EOB Charges"
        showCancelButton
      ></neb-popup-header>
    `;
  }

  renderDescription() {
    return html`
      <div id="${ELEMENTS.description.id}" class="description">
        Select charges to associate to this ERA/EOB.
      </div>
    `;
  }

  __renderPatientFilter() {
    return html`
      <neb-select
        id="${ELEMENTS.selectPatient.id}"
        name="patient"
        class="field"
        tabindex="-1"
        label="Patient"
        .items="${this.__patients}"
        .value="${this.__itemFilters.patient}"
        .onChange="${this.handlers.itemsFilterChange}"
      ></neb-select>
    `;
  }

  __renderAdditionalChargesFilter() {
    return html`
      <neb-switch
        id="${ELEMENTS.buttonAdditionalCharges.id}"
        name="additionalCharges"
        label="Additional Charges"
        class="align-self-center"
        .onChange="${this.handlers.itemsFilterChange}"
        ?on="${this.__itemFilters.additionalCharges}"
      ></neb-switch>
    `;
  }

  __renderApplyButton() {
    return html`
      <neb-button
        id="${ELEMENTS.applyFilterButton.id}"
        label="Apply"
        class="align-self-center"
        .onClick="${this.handlers.applyFilter}"
        ?disabled="${!this.__itemFilters.patient.data.id ||
        !this.__itemFilters.locations.length}"
      ></neb-button>
    `;
  }

  renderFilters() {
    return html`
      <div class="filters">
        <neb-date-picker
          id="${ELEMENTS.dateOfTransactionFrom.id}"
          name="dateOfTransactionFrom"
          class="field"
          tabindex="-1"
          label="Date of Service"
          placeholder="From"
          manualPopoverPosition="${POPOVER_POSITION.CENTER}"
          .selectedDate="${this.__itemFilters.dateOfTransactionFrom}"
          .isDateSelectable="${this.handlers.dateOfTransactionFromSelectable}"
          .onChange="${this.handlers.itemsFilterChange}"
          .pinLabel="${true}"
          momentFlag
        ></neb-date-picker>

        <neb-date-picker
          id="${ELEMENTS.dateOfTransactionTo.id}"
          name="dateOfTransactionTo"
          class="field"
          tabindex="-1"
          label=" "
          placeholder="To"
          manualPopoverPosition="${POPOVER_POSITION.CENTER}"
          .selectedDate="${this.__itemFilters.dateOfTransactionTo}"
          .isDateSelectable="${this.handlers.dateOfTransactionToSelectable}"
          .onChange="${this.handlers.itemsFilterChange}"
          momentFlag
        ></neb-date-picker>

        ${this.__renderPatientFilter()}

        <neb-select
          id="${ELEMENTS.selectLocations.id}"
          name="locations"
          class="field"
          tabindex="-1"
          label="Locations"
          allLabel="Locations"
          .items="${this.__locations}"
          .value="${this.__itemFilters.locations}"
          .onChange="${this.handlers.itemsFilterChange}"
          multiSelect
        ></neb-select>

        ${this.__renderAdditionalChargesFilter()} ${this.__renderApplyButton()}
      </div>
    `;
  }

  renderActionBar() {
    return html`
      <neb-action-bar
        id="${ELEMENTS.actionBar.id}"
        confirmLabel=""
        cancelLabel="Done"
        .onCancel="${this.handlers.apply}"
      ></neb-action-bar>
    `;
  }

  __renderPaginationControl() {
    const pageCount = calculatePageCount(this.__charges.length, 10);

    return html`
      <neb-pagination
        id="${ELEMENTS.paginationControl.id}"
        class="pagination"
        .pageCount="${pageCount}"
        .currentPage="${this.__currentPage}"
        .onPageChanged="${this.handlers.changePage}"
      ></neb-pagination>
    `;
  }

  __getTableConfig() {
    return {
      tableConfig: TABLE_CONFIG,
      itemName: 'charge',
      itemPluralName: 'charges',
    };
  }

  __renderNoItemsMessage() {
    return this.__charges && this.__charges.length
      ? html``
      : html` <p class="no-items-message">No charges.</p> `;
  }

  renderTable() {
    return html`
      <div class="container-table">
        <neb-table-add
          id="${ELEMENTS.table.id}"
          class="header-table"
          .config="${this.__getTableConfig()}"
          .allAdded="${this.__allAdded}"
          .onAddAll="${this.handlers.addAll}"
          .layout="${this.layout}"
          .onAdd="${this.handlers.add}"
          .items="${this.__charges}"
          .selectedItems="${this.__selectedCharges}"
        >
          ${this.__renderNoItemsMessage()}
        </neb-table-add>
      </div>

      ${this.__renderPaginationControl()}
    `;
  }

  renderContent() {
    return html`
      <div class="container">
        ${this.renderHeader()} ${this.renderDescription()}
        ${this.renderFilters()} ${this.renderTable()} ${this.renderActionBar()}
      </div>
    `;
  }
}

customElements.define(
  'neb-overlay-associate-era-eob-charges',
  NebOverlayAssociateEraEobCharges,
);
